import { useMemo } from 'react';
import { Box, Link, Text } from '@nimbus-ds/components';
import { CurrencyType, TaxDto } from '@tiendanube/common';
import { useGetTotalForCharges } from 'domains/Billing/Checkout/hooks';
import { useFormatCurrency } from 'domains/Billing/hooks';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { ChargeDisplayInterface } from '../PaymentDataContent/PaymentDataContent';
import { getTaxesSummary } from '../PaymentDataContent/utils';

interface ChargesSelectionSummaryProps {
  charges: ChargeDisplayInterface[];
  currency?: CurrencyType;
  showTaxes?: boolean;
  showPeriodDiscounts?: boolean;
  extraFee?: number;
  extraTaxes?: TaxDto[];
}

export default function ChargesSelectionSummary({
  charges,
  currency,
  showTaxes = true,
  showPeriodDiscounts = false,
  extraFee = 0,
  extraTaxes = [],
}: Readonly<ChargesSelectionSummaryProps>) {
  const t = useTranslationBilling('checkout.paymentDataContent');
  const formatCurrency = useFormatCurrency();
  const { getTotalForCharges } = useGetTotalForCharges();
  const subTotal = useMemo(
    () =>
      getTotalForCharges(charges, {
        includeGeneralDiscounts: false,
        includePeriodDiscounts: false,
        includeTaxes: false,
      }) + extraFee,
    [charges, extraFee, getTotalForCharges],
  );
  const total = useMemo(
    () =>
      getTotalForCharges(charges, {
        includeGeneralDiscounts: true,
        includePeriodDiscounts: showPeriodDiscounts,
        includeTaxes: showTaxes,
      }) +
      extraFee +
      ((showTaxes &&
        extraTaxes?.reduce((acc, tax) => acc + tax.amountValue, 0)) ||
        0),
    [
      charges,
      extraFee,
      showTaxes,
      extraTaxes,
      showPeriodDiscounts,
      getTotalForCharges,
    ],
  );
  const taxes = useMemo(
    () => getTaxesSummary(charges, extraTaxes, t),
    [charges, t, extraTaxes],
  );
  const totalDiscount = useMemo(
    () =>
      -getTotalForCharges(charges, {
        includeBaseAmount: false,
        includeGeneralDiscounts: true,
        includePeriodDiscounts: showPeriodDiscounts,
        includeTaxes: false,
      }),
    [charges, showPeriodDiscounts, getTotalForCharges],
  );

  const amountCurrency = currency ?? charges[0].amountCurrency;

  function Row({ children }: { children: React.ReactNode }) {
    return (
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {children}
      </Box>
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap="1">
      {total !== subTotal && showTaxes && (
        <Row>
          <Text color="neutral-textLow" fontWeight="bold">
            {t('subtotal')}
          </Text>
          <Text color="neutral-textLow" fontWeight="bold">
            {formatCurrency(subTotal, amountCurrency)}
          </Text>
        </Row>
      )}
      {totalDiscount > 0 && (
        <Row>
          <Text color="success-textLow" fontSize="caption">
            {t('discounts')}
          </Text>
          <Text color="success-textLow" fontSize="caption">
            {`-${formatCurrency(totalDiscount, amountCurrency)}`}
          </Text>
        </Row>
      )}
      {showTaxes &&
        taxes.map((tax) => (
          <Row key={tax.description}>
            <Link
              color="neutral-textLow"
              fontSize="caption"
              as="a"
              href={tax.explanation?.link}
              target="_blank"
            >
              {tax.description}
            </Link>
            <Text color="neutral-textLow" fontSize="caption">
              {formatCurrency(tax.amountValue, tax.amountCurrency)}
            </Text>
          </Row>
        ))}
      <Row>
        <Text color="primary-textHigh" fontSize="highlight" fontWeight="bold">
          {t('total')}
        </Text>
        <Text color="primary-textHigh" fontSize="highlight" fontWeight="bold">
          {formatCurrency(total, amountCurrency)}
        </Text>
      </Row>
    </Box>
  );
}
